import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_CONTENT, FLEX_CONTAINER_COLUMN, FLEX_CONTAINER_COLUMN_CENTERED, JUSTIFY_CONTENT, POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { REF_ATTRIBUTE, createHtmlElement, isWebComponent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { getMapValues } from "@cervval.js/common/src/tools/collection.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CjsToast, TOAST_Z_INDEX, ToastOptions, ToastPlacement, toastTagName } from "../../toast";
import { TOAST_SERVICE, ToastService } from "../toast.service";

/**
 * Default implementation for the ToastService
 */
@Injectable(TOAST_SERVICE)
export class DefaultToastService implements ToastService {

    /** Displayed toast in application */
    public displayedToasts = new Map<number, CjsToast>();// Map keeps order

    /** Container for stacking toasts */
    public toastStackingContainer = createHtmlElement("div", {
        cssClass: [POSITION.ABSOLUTE, FLEX_CONTAINER_COLUMN_CENTERED],
        attributes: {
            [REF_ATTRIBUTE]: "toastStackingContainer"
        },
        style: {
            zIndex: TOAST_Z_INDEX,
            display: Display.NONE
        }
    });

    /** Placement of toasts in app window */
    public toastStackingContainerPlacement = ToastPlacement.BOTTOM_RIGHT;

    /** Toast stacking placement attached to stacking container */
    private _toastStackingPlacementClassesAttached: ToastPlacement | undefined;

    constructor() {
        document.body.append(this.toastStackingContainer);
    }

    /**
     * Show a toast
     * @returns new toast displayed
     */
    public show(toastOrOptions: CjsToast | Partial<ToastOptions>): CjsToast {

        const toast = this.isToast(toastOrOptions) ? toastOrOptions : new CjsToast(toastOrOptions);

        const id = toast.toastId;
        this.displayedToasts.set(id, toast);

        this.toastStackingContainer.prepend(toast);
        this._refreshToastStackingContainerDisplay();

        toast.hiddenEvent.on(() => {
            this.displayedToasts.delete(id);
            this._refreshToastStackingContainerDisplay();
        });

        toast.show({ fromService: true });

        return toast;
    }

    /** 
     * Get a toast displayed with his id
     */
    public getToastById(id: number): CjsToast | undefined {
        return this.displayedToasts.get(id);
    }

    /** 
     * Hide a toast displayed
     */
    public hide(id: number): void {
        const toast = this.getToastById(id);
        if (!toast) {
            getLogService().warn(`Toast not found for id: ${id}, can't hide it`);
            return;
        }

        toast.hide();
        this.displayedToasts.delete(id);
        this._refreshToastStackingContainerDisplay();
    }

    /** Hide all toast displayed */
    public hideAll(): void {
        for (const toast of getMapValues(this.displayedToasts)) {
            this.hide(toast.toastId);
        }
        this._refreshToastStackingContainerDisplay();
    }

    /** Refresh toast stacking container display, depending on toasts left to display */
    private _refreshToastStackingContainerDisplay(): void {
        const placementClasses = this.toastStackingContainerPlacement.split(" ");
        if (!this._toastStackingPlacementClassesAttached) {
            this.toastStackingContainer.classList.add(...placementClasses);
        } else if (this._toastStackingPlacementClassesAttached !== this.toastStackingContainerPlacement) {
            // Placement has changed, remove all classes, re-add new ones
            this._toastStackingPlacementClassesAttached.split(" ").forEach(cssClass => this.toastStackingContainer.classList.remove(cssClass));
            this.toastStackingContainer.classList.add(...placementClasses);
        }
        this._toastStackingPlacementClassesAttached = this.toastStackingContainerPlacement;
        toggleVisible(this.toastStackingContainer, this.displayedToasts.size > 0, Display.FLEX);
    }

    /** @returns true if given object is a Toast component */
    public isToast(obj: anyReallyAny): obj is CjsToast {
        return isWebComponent(obj) && obj.tagName === toastTagName.toUpperCase();
    }
}